import { useElements, useStripe } from "@stripe/react-stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { TriangleAlert } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Link } from "react-router-dom";

import {
  Alert,
  AlertDescription,
  AlertTitle,
} from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Field, FieldLabel } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";

import { stripeUrl, supportURL } from "common/const";
import { PermissionsKeys } from "components/permissions/permission-keys";
import { PermissionsReasons } from "components/permissions/permission-messages";
import { PermissionsWrapper } from "components/permissions/permissions";
import { ReactQueryErrorWrapper } from "components/shared/react-query-error-wrapper";
import { useIdentity } from "contexts/identity-context";
import * as QueryKeys from "data";
import { addPaymentMethod } from "data/mutations-common";
import { fetchPaymentMethod, fetchSubscription } from "data/queries-common";
import { resolveIdentityId } from "utils/identity";

import { EditPaymentMethodDialogue } from "./editPaymentDialogue";

interface PaymentMethodInfoProps {
  setPaymentMethod?: Dispatch<SetStateAction<boolean>>;
  hideTitle?: boolean;
}

const PaymentMethodInfo = (props: PaymentMethodInfoProps) => {
  const [identityState] = useIdentity();
  const identityId = resolveIdentityId(identityState);
  const stripe = useStripe();
  const stripeElements = useElements();
  const queryClient = useQueryClient();
  const [editingCC, setEditingCC] = useState(false);

  const paymentMethodQuery = useQuery({
    queryKey: [QueryKeys.paymentMethod, identityId],
    queryFn: () => fetchPaymentMethod(),
  });

  const subscriptionQuery = useQuery({
    queryKey: [QueryKeys.subscription, identityId],
    queryFn: () => fetchSubscription(),
  });

  useEffect(() => {
    if (
      paymentMethodQuery.isSuccess &&
      paymentMethodQuery.data &&
      props.setPaymentMethod
    ) {
      props.setPaymentMethod(true);
    }
  }, [paymentMethodQuery.isSuccess]);

  const paymentMethodMutation = useMutation({
    mutationFn: () => addPaymentMethod(stripe, stripeElements, setEditingCC),
    onSuccess: (newPaymentMethod) => {
      queryClient.setQueryData(
        [QueryKeys.paymentMethod, identityId],
        newPaymentMethod,
      );
    },
  });

  const handleOnClose = () => {
    paymentMethodMutation.reset();
    setEditingCC(false);
  };

  return (
    <>
      <Card>
        {!props.hideTitle && (
          <CardHeader>
            <CardTitle>Payment Method</CardTitle>
          </CardHeader>
        )}
        <CardContent className="space-y-3">
          <ReactQueryErrorWrapper
            queries={[paymentMethodQuery, subscriptionQuery]}
            mutations={[paymentMethodMutation]}
          />

          {paymentMethodQuery.isPending && (
            <div className="space-y-2">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-10 w-full" />
            </div>
          )}

          {paymentMethodQuery.isSuccess && paymentMethodQuery.data && (
            <>
              <Field>
                <FieldLabel htmlFor="card-number">Card Number</FieldLabel>
                <Input
                  id="card-number"
                  disabled
                  value={`**** **** **** ${paymentMethodQuery.data.last4}`}
                />
              </Field>
              <Field>
                <FieldLabel htmlFor="card-expiry">Expiration</FieldLabel>
                <Input
                  id="card-expiry"
                  disabled
                  value={`${String(
                    paymentMethodQuery.data.expireMonth,
                  ).padStart(2, "0")}/${paymentMethodQuery.data.expireYear}`}
                />
              </Field>
            </>
          )}

          {paymentMethodQuery.isSuccess && paymentMethodQuery.data === null && (
            <Alert variant="warning">
              <TriangleAlert />
              <AlertTitle>No payment method currently configured</AlertTitle>
              <AlertDescription>
                Click the update card button below to add a payment method
              </AlertDescription>
            </Alert>
          )}

          {subscriptionQuery.isPending && <Skeleton className="h-9 w-32" />}

          <PermissionsWrapper
            keys={[PermissionsKeys.paymentMethodUpdate]}
            messages={{
              [PermissionsReasons.EXTERNALLY_MANAGED]: (
                <Alert variant="info">
                  <AlertDescription>
                    Your subscription is managed outside of hub. Please contact{" "}
                    <Link
                      className="underline"
                      target="_blank"
                      to={{ pathname: supportURL }}
                    >
                      support
                    </Link>{" "}
                    to change your payment method.
                  </AlertDescription>
                </Alert>
              ),
            }}
          >
            <Button onClick={() => setEditingCC(true)}>Update Card</Button>
          </PermissionsWrapper>
        </CardContent>
      </Card>

      <EditPaymentMethodDialogue
        open={editingCC}
        handleOnClose={handleOnClose}
        stripeUrl={stripeUrl}
        paymentMethodMutation={paymentMethodMutation}
      />
    </>
  );
};

export default PaymentMethodInfo;
